import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Scale, CheckCircle, Clock, ArrowRight, FileText, Users, XCircle, AlertTriangle, Stethoscope, Mail, Gavel, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LegislativePage = () => {
  const [victories, setVictories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchVictories = async () => {
      try {
        const response = await axios.get(`${API}/legislative-victories`);
        setVictories(response.data.victories);
      } catch (error) {
        console.error("Error fetching legislative victories:", error);
        setVictories([]);
      } finally {
        setLoading(false);
      }
    };

    fetchVictories();
  }, []);

  return (
    <div className="pt-20" data-testid="legislative-page">
      {/* Hero Section */}
      <section
        className="py-24 md:py-32 relative bg-cover bg-center"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1513023001678-6927b70dc4a0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwzfHxUZXhhcyUyMFN0YXRlJTIwQ2FwaXRvbCUyMGJ1aWxkaW5nJTIwaW50ZXJpb3IlMjBsZWdpc2xhdGl2ZXxlbnwwfHx8fDE3NzAxNDM5Nzd8MA&ixlib=rb-4.1.0&q=85')`
        }}>

        <div className="absolute inset-0 bg-slate-900/80"></div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <p className="text-amber-400 font-medium tracking-widest uppercase text-sm mb-4">
              Legislative Victories
            </p>
            <h1 className="font-serif text-5xl md:text-6xl font-bold text-white leading-[1.1] mb-6">
              Landmark <span className="text-amber-400 italic">Legislation</span> for Sickle Cell
            </h1>
            <p className="text-slate-300 text-lg md:text-xl leading-relaxed">
              Our organization directly led and drove the passage of landmark sickle cell legislation in Texas through years of hands-on advocacy, coalition building, and sustained engagement—well before broader stakeholder participation and involvement—resulting in improved care, access, and support for thousands of patients and families across the state.
            </p>
          </div>
        </div>
      </section>

      {/* Stats Banner */}
      <section className="py-12 bg-amber-700">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <p className="font-serif text-4xl font-bold text-white">7</p>
              <p className="text-amber-100 text-sm uppercase tracking-wider">Bills Championed</p>
            </div>
            <div>
              <p className="!font-serif !font-bold !text-4xl text-white">4</p>
              <p className="text-amber-100 text-sm uppercase tracking-wider">Passed Into Law</p>
            </div>
            <div>
              <p className="!font-serif !font-bold !text-4xl text-white">10K+</p>
              <p className="text-amber-100 text-sm uppercase tracking-wider">Lives Impacted</p>
            </div>
            <div>
              <p className="!font-serif !font-bold !text-4xl text-white">3</p>
              <p className="text-amber-100 text-sm uppercase tracking-wider">Active Bills</p>
            </div>
          </div>
        </div>
      </section>

      {/* Coalition & Consistency Section */}
      <section className="py-20 bg-white" data-testid="coalition-section">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-12 items-center">
            <div className="md:col-span-7">
              <p className="text-teal-700 font-medium tracking-widest uppercase text-sm mb-4">
                A Decade of Unwavering Advocacy
              </p>
              <h2 className="font-serif text-4xl md:text-5xl font-semibold text-slate-900 leading-tight mb-6">
                Consistency. Coalition. Change.
              </h2>
              <p className="text-slate-700 text-lg leading-relaxed mb-5">
                Since <span className="font-semibold text-slate-900">2016</span>, the Sickle Cell Association has shown up — session after session, hearing after hearing — to put sickle cell disease on the public-policy agenda of Texas and the nation.
              </p>
              <p className="text-slate-700 text-lg leading-relaxed mb-5">
                We don&apos;t advocate alone. We build coalitions with the <span className="font-semibold">Rare Disease Legislative Advocates</span> and a growing network of patient-advocacy organizations across the country. Our voice has been amplified by lawmakers like <span className="font-semibold">Rep. Jarvis Johnson</span>, <span className="font-semibold">Rep. Lauren Simmons</span>, and <span className="font-semibold">Sen. Borris Miles</span>, and recognized by leaders including <span className="font-semibold">Mayor Sylvester Turner</span>, <span className="font-semibold">Congresswoman Sheila Jackson Lee</span>, and <span className="font-semibold">U.S. Senator Ted Cruz</span>.
              </p>
              <p className="text-slate-700 text-lg leading-relaxed">
                The result is a track record measured in passed bills, established registries, committee testimony, and warriors who finally feel heard — from <span className="italic">HB 1633</span> (2017) through <span className="italic">HCR 86</span>, <span className="italic">HB 3673</span>, and the historic <span className="italic">HB 107 Sickle Cell Registry</span> victory of 2025. The work continues.
              </p>
            </div>
            <div className="md:col-span-5 grid grid-cols-2 gap-3">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
                <p className="font-serif text-3xl font-bold text-amber-800">2016</p>
                <p className="text-amber-900 text-sm mt-1">First Capitol testimony</p>
              </div>
              <div className="bg-teal-50 border border-teal-200 rounded-xl p-5">
                <p className="font-serif text-3xl font-bold text-teal-800">9+</p>
                <p className="text-teal-900 text-sm mt-1">Years of policy advocacy</p>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-5">
                <p className="font-serif text-3xl font-bold text-slate-900">12+</p>
                <p className="text-slate-700 text-sm mt-1">Coalition partners</p>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
                <p className="font-serif text-3xl font-bold text-amber-800">2019</p>
                <p className="text-amber-900 text-sm mt-1">National Advocate Award</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The Road to HB 107 — Texas Sickle Cell Registry */}
      <section className="py-20 bg-slate-50" data-testid="registry-story-section">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <p className="text-amber-700 font-medium tracking-widest uppercase text-sm mb-4">
              A Four-Year Journey
            </p>
            <h2 className="font-serif text-4xl md:text-5xl font-semibold text-slate-900 leading-tight mb-4">
              The Road to HB 107
            </h2>
            <p className="text-xl text-slate-600 font-serif italic">
              How Texas built its Sickle Cell Disease Registry
            </p>
            <p className="text-slate-600 mt-6 max-w-3xl mx-auto leading-relaxed">
              Behind every law is a story of persistence. Click any chapter below to read how a 2021 idea — sparked by SCAH&apos;s research into the Texas Cancer Registry — grew into a multi-state, multi-session coalition effort that delivered the historic 2025 victory.
            </p>
          </div>

          <Accordion type="single" collapsible defaultValue="chapter-1" className="bg-white rounded-2xl shadow-sm border border-slate-200 px-6 md:px-10" data-testid="registry-accordion">
            {/* Chapter 1 */}
            <AccordionItem value="chapter-1" data-testid="registry-chapter-1">
              <AccordionTrigger className="py-6 hover:no-underline">
                <div className="flex items-start gap-4 text-left">
                  <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <FileText className="w-5 h-5 text-amber-800" />
                  </div>
                  <div>
                    <p className="text-amber-700 text-xs font-semibold tracking-wider uppercase">2017 – 2019 · Foundation</p>
                    <p className="font-serif text-xl font-semibold text-slate-900 mt-1">Building the Legislative Bedrock</p>
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pb-6 pl-16 text-slate-700 leading-relaxed space-y-3">
                <p>
                  Long before a registry bill was drafted, the Sickle Cell Association began the patient work of cultivating legislative champions. State Representative <span className="font-semibold">Jarvis Johnson</span> emerged as the most consistent voice for sickle cell in the Texas House.
                </p>
                <p>
                  In the <span className="font-semibold">86th Legislative Session (2019)</span>, Rep. Johnson filed <span className="font-semibold">four sickle cell-related bills</span>. All four passed the House and Senate. All four were signed into law by the Governor. That single session set the precedent that sickle cell legislation could — and would — move through Austin.
                </p>
              </AccordionContent>
            </AccordionItem>

            {/* Chapter 2 */}
            <AccordionItem value="chapter-2" data-testid="registry-chapter-2">
              <AccordionTrigger className="py-6 hover:no-underline">
                <div className="flex items-start gap-4 text-left">
                  <div className="w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                    <Stethoscope className="w-5 h-5 text-teal-800" />
                  </div>
                  <div>
                    <p className="text-teal-700 text-xs font-semibold tracking-wider uppercase">Early 2021 · Strategy</p>
                    <p className="font-serif text-xl font-semibold text-slate-900 mt-1">Studying What Already Works in Texas</p>
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pb-6 pl-16 text-slate-700 leading-relaxed space-y-3">
                <p>
                  On <span className="font-semibold">March 11, 2021</span>, SCAH President & CEO <span className="font-semibold">Tonya Prince</span> wrote to legislative staff with a strategy: don&apos;t reinvent the wheel.
                </p>
                <blockquote className="border-l-4 border-amber-400 pl-4 italic text-slate-700 bg-amber-50/40 py-3 pr-4 rounded-r-lg">
                  &ldquo;I found some examples of how the Cancer Registry was set up as well as the Syndromic Surveillance Reporting. If we can duplicate these same rules to include sickle cell disease, we may have a chance at yielding something fruitful this session.&rdquo;
                  <footer className="text-xs text-slate-500 mt-2 not-italic">— Tonya Prince, March 11, 2021</footer>
                </blockquote>
                <p>
                  Her key insight pushed the bill text beyond standard models: <span className="font-semibold">reporting requirements should include &ldquo;Payers&rdquo; — insurance carriers — in addition to healthcare providers and institutions</span>. That single edit would ensure the registry captured Medicaid claims data, where 61% of sickle cell care is delivered.
                </p>
              </AccordionContent>
            </AccordionItem>

            {/* Chapter 3 */}
            <AccordionItem value="chapter-3" data-testid="registry-chapter-3">
              <AccordionTrigger className="py-6 hover:no-underline">
                <div className="flex items-start gap-4 text-left">
                  <div className="w-12 h-12 rounded-full bg-slate-200 flex items-center justify-center flex-shrink-0">
                    <Gavel className="w-5 h-5 text-slate-800" />
                  </div>
                  <div>
                    <p className="text-slate-600 text-xs font-semibold tracking-wider uppercase">March 2021 · Bills Filed</p>
                    <p className="font-serif text-xl font-semibold text-slate-900 mt-1">HB 3672 &amp; HB 3673 Enter the Record</p>
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pb-6 pl-16 text-slate-700 leading-relaxed space-y-3">
                <p>
                  Rep. Johnson filed two companion bills in the <span className="font-semibold">87th Legislative Session</span>:
                </p>
                <ul className="space-y-3 ml-2">
                  <li className="flex gap-3">
                    <span className="font-semibold text-amber-800 flex-shrink-0">HB 3672</span>
                    <span>Expand Texas Medicaid eligibility to all people living with sickle cell disease.</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="font-semibold text-amber-800 flex-shrink-0">HB 3673</span>
                    <span>Establish and maintain a state registry of patients diagnosed with sickle cell disease.</span>
                  </li>
                </ul>
                <p>
                  The case the bills made to legislators was stark: roughly <span className="font-semibold">7,200 Texans</span> were estimated to be living with sickle cell disease — but no one had an accurate count. The average 45-year-old patient had already spent an estimated <span className="font-semibold">$1 million</span> on care, with annual costs peaking near <span className="font-semibold">$30,000</span>. About <span className="font-semibold">61%</span> of patients relied on Medicaid.
                </p>
                <blockquote className="border-l-4 border-teal-400 pl-4 italic text-slate-700 bg-teal-50/40 py-3 pr-4 rounded-r-lg">
                  &ldquo;Texas should be a national leader in sickle cell care and research, especially since we house the world-renowned Texas Medical Center. The bills I filed will move us further to eliminating health disparities and making Texas a leader in sickle cell care for this often overlooked population.&rdquo;
                  <footer className="text-xs text-slate-500 mt-2 not-italic">— Rep. Jarvis Johnson, 2021</footer>
                </blockquote>
              </AccordionContent>
            </AccordionItem>

            {/* Chapter 4 */}
            <AccordionItem value="chapter-4" data-testid="registry-chapter-4">
              <AccordionTrigger className="py-6 hover:no-underline">
                <div className="flex items-start gap-4 text-left">
                  <div className="w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-5 h-5 text-teal-800" />
                  </div>
                  <div>
                    <p className="text-teal-700 text-xs font-semibold tracking-wider uppercase">Late April 2021 · Committee</p>
                    <p className="font-serif text-xl font-semibold text-slate-900 mt-1">Heard Favorably by Public Health</p>
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pb-6 pl-16 text-slate-700 leading-relaxed space-y-3">
                <p>
                  HB 3673 was heard before the <span className="font-semibold">Texas House Committee on Public Health</span> in the final week of April 2021. SCAH advocates testified. The committee received the bill favorably — the first time a Texas sickle cell registry had cleared this threshold.
                </p>
                <p>
                  The bill ultimately did not become law in 2021, but the legislative record had been set, the language was on file, and a coalition was forming.
                </p>
              </AccordionContent>
            </AccordionItem>

            {/* Chapter 5 */}
            <AccordionItem value="chapter-5" data-testid="registry-chapter-5">
              <AccordionTrigger className="py-6 hover:no-underline">
                <div className="flex items-start gap-4 text-left">
                  <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-amber-800" />
                  </div>
                  <div>
                    <p className="text-amber-700 text-xs font-semibold tracking-wider uppercase">Mid-2021 · Coalition</p>
                    <p className="font-serif text-xl font-semibold text-slate-900 mt-1">A National Conversation Begins</p>
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pb-6 pl-16 text-slate-700 leading-relaxed space-y-3">
                <p>
                  As HB 3673 worked its way through committee, the conversation widened. <span className="font-semibold">Dr. Stephanie Ibemere</span>, a postdoctoral fellow at the Duke University School of Nursing, reached out to SCAH:
                </p>
                <blockquote className="border-l-4 border-slate-400 pl-4 italic text-slate-700 bg-slate-50 py-3 pr-4 rounded-r-lg">
                  &ldquo;Andre Harris, in conjunction with Representative Jarvis Johnson, has introduced House Bill 3673 to the Public Health committee, which petitions the establishment of a sickle cell disease registry in Texas. We&apos;d like Texas to join the ranks of North Carolina and the other eight states which have state-level registries for sickle cell disease.&rdquo;
                  <footer className="text-xs text-slate-500 mt-2 not-italic">— Dr. Stephanie Ibemere, Duke University School of Nursing</footer>
                </blockquote>
                <p>
                  SCAH connected with the team behind North Carolina&apos;s Brain Health Registry — and with <span className="font-semibold">André Harris</span> (then a Texas House social work intern and MSW/PhD student) — to study how other state registries had been built, governed, and funded. Texas was no longer trying to do this alone.
                </p>
              </AccordionContent>
            </AccordionItem>

            {/* Chapter 6 */}
            <AccordionItem value="chapter-6" data-testid="registry-chapter-6">
              <AccordionTrigger className="py-6 hover:no-underline">
                <div className="flex items-start gap-4 text-left">
                  <div className="w-12 h-12 rounded-full bg-slate-200 flex items-center justify-center flex-shrink-0">
                    <Users className="w-5 h-5 text-slate-800" />
                  </div>
                  <div>
                    <p className="text-slate-600 text-xs font-semibold tracking-wider uppercase">2021 – 2024 · Persistence</p>
                    <p className="font-serif text-xl font-semibold text-slate-900 mt-1">Session After Session, Voice After Voice</p>
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pb-6 pl-16 text-slate-700 leading-relaxed space-y-3">
                <p>
                  Across the 88th Legislative Session and into the 89th, SCAH continued to show up — at hearings, at advocacy days, at coalition tables. Champions in the Senate, including <span className="font-semibold">Sen. Borris Miles</span>, joined the effort. House recognition resolutions like <span className="font-semibold">HCR 86</span> kept sickle cell on the legislative agenda even in sessions where the registry bill itself didn&apos;t make it to the Governor&apos;s desk.
                </p>
                <p>
                  In 2019, Tonya Prince was honored as a <span className="font-semibold">National Advocate</span> at the RareVoice Awards — recognition that the work being done in Texas was being watched, and modeled, across the country.
                </p>
              </AccordionContent>
            </AccordionItem>

            {/* Chapter 7 */}
            <AccordionItem value="chapter-7" data-testid="registry-chapter-7">
              <AccordionTrigger className="py-6 hover:no-underline">
                <div className="flex items-start gap-4 text-left">
                  <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-5 h-5 text-amber-800" />
                  </div>
                  <div>
                    <p className="text-amber-700 text-xs font-semibold tracking-wider uppercase">2025 · Victory</p>
                    <p className="font-serif text-xl font-semibold text-slate-900 mt-1">HB 107 — Texas Sickle Cell Registry, Passed</p>
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pb-6 pl-16 text-slate-700 leading-relaxed space-y-3">
                <p>
                  In the <span className="font-semibold">89th Legislative Session</span>, the bill that began as HB 3673 in 2021 was reintroduced — refined, sharpened, and backed by a four-year coalition — as <span className="font-semibold">HB 107: The Sickle Cell Registry Bill</span>.
                </p>
                <p>
                  It passed.
                </p>
                <p>
                  Texas joined the small but growing number of states with a formal, state-funded sickle cell disease registry — a public-health infrastructure that will, for the first time, give researchers, clinicians, and policymakers an accurate picture of who is living with sickle cell disease in Texas, where they are, and what care they&apos;re receiving.
                </p>
                <p className="font-serif text-lg text-amber-900 italic pt-2">
                  Four years. Two bills. One persistent coalition. One historic win.
                </p>
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          <div className="mt-10 text-center">
            <p className="text-slate-600 text-sm">
              Want to support the next chapter?{" "}
              <Link to="/donate" className="text-amber-700 font-semibold hover:underline" data-testid="registry-cta-donate">
                Donate
              </Link>
              {" "}or{" "}
              <Link to="/members" className="text-amber-700 font-semibold hover:underline" data-testid="registry-cta-join">
                join as a member
              </Link>
              .
            </p>
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-24 bg-slate-50" data-testid="timeline-section">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-teal-700 font-medium tracking-widest uppercase text-sm mb-4">
              Our Legislative Journey 2019-2025
            </p>
            <h2 className="font-serif text-4xl md:text-5xl font-semibold text-slate-900">
              A Timeline of Progress
            </h2>
            <p className="text-slate-600 mt-4 max-w-2xl mx-auto">
              From establishing the Sickle Cell Task Force to fighting for a statewide registry, our advocacy spans multiple legislative sessions with champions like Rep. Jarvis Johnson, Rep. Lauren Simmons, and Senator Borris Miles.
            </p>
          </div>

          {loading ?
          <div className="flex justify-center py-12">
              <div className="spinner"></div>
            </div> :

          <div className="relative">
              {/* Vertical Timeline Line */}
              <div className="absolute left-1/2 transform -translate-x-1/2 w-0.5 h-full bg-gradient-to-b from-amber-400 to-amber-600 hidden md:block"></div>

              <div className="space-y-12">
                {victories.map((victory, index) =>
              <div
                key={victory.id}
                className={`relative flex flex-col md:flex-row items-center gap-8 ${
                index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`
                }
                data-testid={`victory-${victory.id}`}>

                    {/* Timeline Node */}
                    <div className="absolute left-1/2 transform -translate-x-1/2 hidden md:flex">
                      <div className="w-4 h-4 bg-amber-500 rounded-full border-4 border-amber-100 z-10"></div>
                    </div>

                    {/* Year Badge */}
                    <div className="md:w-1/2 flex justify-end md:pr-16">
                      <div className={`${index % 2 === 0 ? 'md:text-right' : 'md:text-left md:order-2'}`}>
                        <span className="inline-block bg-slate-900 text-white font-bold text-lg px-4 py-2 rounded">
                          {victory.year}
                        </span>
                      </div>
                    </div>

                    {/* Content Card */}
                    <div className="md:w-1/2 md:pl-16">
                      <Card className="legislative-card bg-white shadow-lg hover:shadow-xl transition-shadow">
                        <CardContent className="p-8">
                          <div className="flex items-center gap-3 mb-4">
                            <span className="bg-amber-100 text-amber-800 font-bold text-sm px-3 py-1 rounded">
                              {victory.bill}
                            </span>
                            {victory.status === "Passed" ? (
                              <span className="flex items-center gap-1 text-teal-600 text-sm font-medium">
                                <CheckCircle className="w-4 h-4" />
                                Passed
                              </span>
                            ) : victory.status === "Vetoed" ? (
                              <span className="flex items-center gap-1 text-red-600 text-sm font-medium">
                                <XCircle className="w-4 h-4" />
                                Vetoed
                              </span>
                            ) : (
                              <span className="flex items-center gap-1 text-amber-600 text-sm font-medium">
                                <Clock className="w-4 h-4" />
                                In Progress
                              </span>
                            )}
                          </div>
                          <h3 className="font-serif text-2xl font-semibold text-slate-900 mb-3">
                            {victory.title}
                          </h3>
                          <p className="text-slate-600 leading-relaxed mb-4">
                            {victory.description}
                          </p>
                          <p className="text-sm text-slate-500">
                            <span className="font-medium">Champion:</span> {victory.champion}
                          </p>
                        </CardContent>
                      </Card>
                    </div>
                  </div>
              )}
              </div>
            </div>
          }
        </div>
      </section>

      {/* Jarvis Johnson Section */}
      <section className="py-24 bg-white" data-testid="jarvis-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-amber-700 font-medium tracking-widest uppercase text-sm mb-4">
                Legislative Champion
              </p>
              <h2 className="font-serif text-4xl md:text-5xl font-semibold text-slate-900 mb-6">
                Working with Rep. Jarvis Johnson
              </h2>
              <p className="text-slate-600 text-lg leading-relaxed mb-6">
                Representative Jarvis Johnson has been an unwavering advocate for the sickle cell community in Texas. His leadership and dedication have been instrumental in advancing critical legislation.
              </p>
              <p className="text-slate-600 leading-relaxed mb-8">
                Through our partnership with Rep. Johnson, we continue to champion HB 181, which would establish a comprehensive statewide registry for sickle cell disease patients—a groundbreaking step toward better tracking, research, and care coordination across Texas.
              </p>
              <Card className="bg-slate-50 border-l-4 border-l-amber-600">
                <CardContent className="p-6">
                  <h4 className="font-serif text-xl font-semibold text-slate-900 mb-2">HB 181 - Statewide Registry</h4>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    This legislation would create a comprehensive database to track sickle cell disease cases, enabling better healthcare planning, research opportunities, and improved patient outcomes across the state.
                  </p>
                </CardContent>
              </Card>
            </div>
            <div className="relative">
              <div className="bg-slate-100 rounded-sm p-12 text-center">
                <Scale className="w-24 h-24 text-amber-700 mx-auto mb-6" />
                <h3 className="font-serif text-2xl font-semibold text-slate-900 mb-2">Legislative Advocacy</h3>
                <p className="text-slate-600">Fighting for policy change at the Texas Capitol</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Impact Section */}
      <section className="py-24 bg-slate-900" data-testid="impact-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-amber-400 font-medium tracking-widest uppercase text-sm mb-4">
              Our Impact
            </p>
            <h2 className="font-serif text-4xl md:text-5xl font-semibold text-white">
              What These Victories Mean
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-8 text-center">
                <FileText className="w-12 h-12 text-amber-500 mx-auto mb-6" />
                <h3 className="font-serif text-xl font-semibold text-white mb-3">Better Care Standards</h3>
                <p className="text-slate-400 leading-relaxed">
                  The Sickle Cell Task Force has set new standards for care and treatment protocols across Texas healthcare systems.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-8 text-center">
                <Users className="w-12 h-12 text-teal-500 mx-auto mb-6" />
                <h3 className="font-serif text-xl font-semibold text-white mb-3">Pain Management Access</h3>
                <p className="text-slate-400 leading-relaxed">
                  Opioid exemptions ensure patients can access critical pain management without bureaucratic barriers.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-8 text-center">
                <Scale className="w-12 h-12 text-amber-500 mx-auto mb-6" />
                <h3 className="font-serif text-xl font-semibold text-white mb-3">Increased Awareness</h3>
                <p className="text-slate-400 leading-relaxed">
                  Awareness license plates have raised visibility and funding for sickle cell programs across the state.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-amber-600 to-amber-700">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-serif text-4xl font-bold text-white mb-6">
            Support Our Legislative Efforts
          </h2>
          <p className="text-amber-100 text-lg leading-relaxed mb-8">
            Help us continue fighting for legislative change that improves the lives of sickle cell patients and their families.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/donate">
              <Button className="bg-white text-amber-700 hover:bg-amber-50 px-10 py-6 font-sans font-bold tracking-wide uppercase text-sm">
                Donate Now
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="border-2 border-white text-white hover:bg-white/10 px-10 py-6 font-sans font-bold uppercase text-sm">
                Get Involved
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>);

};

export default LegislativePage;